import { EditorSection } from '../EditorSection';
import { useCaps, usePlotEditorContext } from '../PlotEditorContext';
import { usePlotsStore } from '@/store/plots';
import { SeriesControls } from '../series-controls';
import { BarsControls } from '../bars-controls';
import { PointControls } from '../point-controls';
import { ThemeControls } from '../theme-controls';
import { useState, useEffect, useRef } from 'react';

/**
 * Colors & Styles Tab
 * Primary: Theme & palettes, global typography, series appearance, bar appearance
 * Advanced: Special series colors, per-point overrides
 */
export function ColorsStylesTab() {
  const caps = useCaps();
  const { plotlyJson, onUpdate } = usePlotEditorContext();
  const { selectedPoint, selection, editorUI } = usePlotsStore();
  const [expandedSection, setExpandedSection] = useState<string | undefined>(undefined);
  const lastFocusHintTs = useRef<number>(0);
  
  // Auto-expand sections based on selection or focusHint
  useEffect(() => {
    const hint = editorUI.focusHint;
    const isPointSelected = selection.kind === 'point' || selectedPoint !== null;
    const isSeriesSelected = selection.kind === 'series' || selection.kind === 'legendItem';
    
    // Check focusHint first (one-shot) - this takes priority
    if (hint && hint.expandSectionId && hint.ts && hint.ts !== lastFocusHintTs.current) {
      lastFocusHintTs.current = hint.ts;
      if (hint.expandSectionId === 'point-controls' || hint.expandSectionId === 'series-appearance') {
        setExpandedSection(hint.expandSectionId);
        return;
      }
    }
    
    // Fallback to selection-based expansion (only if no active focusHint)
    if (!hint || !hint.expandSectionId) {
      if (isPointSelected && expandedSection !== 'point-controls') {
        setExpandedSection('point-controls');
      } else if (isSeriesSelected && expandedSection !== 'series-appearance') {
        setExpandedSection('series-appearance');
      }
    }
  }, [selection, selectedPoint, editorUI.focusHint, expandedSection]);

  return (
    <div className="space-y-4">
      {/* Section heading */}
      <div className="space-y-1">
        <h3 className="text-xs font-semibold text-foreground">Colors & Styles</h3>
        <p className="text-[11px] text-muted-foreground">
          Set color palettes, typography, and series styling.
        </p>
      </div>

      {/* Theme & Palettes */}
      {caps.supports.journalTemplates && (
        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Theme & Palettes
          </p>
          <ThemeControls plotlyJson={plotlyJson} onUpdate={onUpdate} />
        </div>
      )}

      {/* Series Appearance */}
      <EditorSection
        id="series-appearance"
        title="Series Appearance"
        collapsible
        defaultCollapsed={false}
        value={expandedSection === 'series-appearance' ? 'series-appearance' : undefined}
        onValueChange={(v) => setExpandedSection(v)}
      >
        <SeriesControls plotlyJson={plotlyJson} onUpdate={onUpdate} />
        {caps.traces.hasBars && <BarsControls plotlyJson={plotlyJson} onUpdate={onUpdate} />}
      </EditorSection>

      {caps.supports.perPointStyle && (
        <EditorSection
          id="point-controls"
          title="Advanced"
          collapsible
          defaultCollapsed={true}
          value={expandedSection === 'point-controls' ? 'point-controls' : undefined}
          onValueChange={(v) => setExpandedSection(v)}
        >
          <PointControls plotlyJson={plotlyJson} selectedPoint={selectedPoint} onUpdate={onUpdate} />
        </EditorSection>
      )}
    </div>
  );
}

